/**
 * DocumentStateManagerRFC - RFC-compliant document state management
 * 
 * This integrates with the new RFC architecture:
 * 1. Uses InMemorySourceOfTruth as the single source of truth
 * 2. Provides UI-friendly computed views of the data
 * 3. Handles user interactions and updates InMemorySourceOfTruth
 * 4. Listens to changes from InMemorySourceOfTruth for reactive updates
 */

import { InMemorySourceOfTruth, Property, ObjectMetadata } from './InMemorySourceOfTruth';
import { DocumentPersistenceManager } from './DocumentPersistenceManager';
import { SQLiteService } from './SQLiteService';
import { clientStateManager } from './ClientStateManager';

export interface UIDocumentObject {
  object_id: string;
  parent_id: string | null;
  object_type: string;
  is_tombstoned: boolean;
  // UI-friendly computed fields
  title?: string;
  content?: string;
  position?: string;
  created_at?: number;
  updated_at?: number;
  // Raw properties for advanced usage
  properties: Map<string, any>;
}

export interface DocumentStateRFC {
  documentId: string;
  title: string;
  objects: Map<string, UIDocumentObject>;
  pages: UIDocumentObject[];
  isLoaded: boolean;
  isDirty: boolean;
  lastModified?: number;
}

export interface DocumentStateListener {
  (state: DocumentStateRFC): void;
}

export class DocumentStateManagerRFC {
  private sourceOfTruth: InMemorySourceOfTruth;
  private persistenceManager: DocumentPersistenceManager;
  private sqliteService: SQLiteService;
  private documentId: string;
  private replicaId: number;
  private listeners: Set<DocumentStateListener> = new Set();
  private state: DocumentStateRFC;
  private static fallbackTimestampCounter = 0;

  constructor(
    documentId: string,
    sourceOfTruth: InMemorySourceOfTruth,
    persistenceManager: DocumentPersistenceManager,
    sqliteService: SQLiteService,
    replicaId?: number
  ) {
    this.documentId = documentId;
    this.sourceOfTruth = sourceOfTruth;
    this.persistenceManager = persistenceManager;
    this.sqliteService = sqliteService;
    this.replicaId = replicaId ?? (() => {
      try {
        return clientStateManager.getReplicaId();
      } catch {
        return 1; // Default replica ID for testing
      }
    })();

    // Initialize empty state
    this.state = {
      documentId,
      title: documentId,
      objects: new Map(),
      pages: [],
      isLoaded: false,
      isDirty: false
    };

    // Set up reactive updates when source of truth changes
    this.setupReactiveUpdates();
  }

  /**
   * Get next logical timestamp with fallback
   */
  private getNextLogicalTimestamp(): number {
    try {
      return clientStateManager.getNextLogicalTimestamp();
    } catch {
      // Fallback with monotonic counter for testing or when ClientStateManager is not initialized
      return Date.now() + (++DocumentStateManagerRFC.fallbackTimestampCounter);
    }
  }

  /**
   * Get current state (read-only)
   */
  getState(): Readonly<DocumentStateRFC> {
    return { ...this.state };
  }

  /**
   * Load document from database into InMemorySourceOfTruth
   */
  async loadFromDatabase(): Promise<void> {

    try {
      // Use the persistence manager to load structural properties (document + pages only)
      const structuralObjectIds = await this.getStructuralObjectIds();
      const propertiesLoaded = await this.persistenceManager.loadPropertiesFromDisk(structuralObjectIds);
    
    // Debug: Log all properties in source of truth
    const allProperties = this.sourceOfTruth.getPropertiesSince(0);

      // Recompute hashes after loading
      this.sourceOfTruth.recomputeHashes();

    // Update our UI state from the source of truth
    this.updateStateFromSourceOfTruth();
    
    // Notify listeners that the state has been updated
    this.notifyListeners();
    } catch (error) {
      console.error(`❌ [RFC STATE] Failed to load document ${this.documentId}:`, error);
      throw error;
    }
  }

  /**
   * Update a property on an object
   */
  updateProperty(objectId: string, propertyName: string, value: any): void {

    // Get current replica info
    const timestamp = Date.now();
    const logicalTimestamp = this.getNextLogicalTimestamp();
    
    // Generate LWW counter using the same strategy as SQLiteService:
    // Find max LWW counter for this object and increment by 1
    const objectProperties = this.sourceOfTruth.getObjectProperties(objectId);
    const maxCounter = Math.max(0, ...objectProperties
      .filter(p => p.lwwCounter !== null)
      .map(p => p.lwwCounter || 0)
    );
    const lwwCounter = maxCounter + 1;
    const lwwRandom = Math.floor(Math.random() * Number.MAX_SAFE_INTEGER);

    // Add property to source of truth
    this.sourceOfTruth.addProperty({
      objectId,
      propertyName,
      value,
      lwwCounter, // Use incrementing counter (consistent with SQLiteService)
      lwwRandom, // Random for tie-breaking
      replicaId: this.replicaId,
      logicalTimestamp,
      source: 'user',
      addedToMemoryAt: timestamp
    });

    // Update UI state immediately
    this.updateStateFromSourceOfTruth();
    
    // Mark as dirty
    this.state.isDirty = true;
    this.state.lastModified = timestamp;
    this.notifyListeners();
  }

  /**
   * Create a new object
   */
  createObject(objectId: string, objectType: string, parentId?: string): void {

    const timestamp = Date.now();
    const metadataLogicalTimestamp = this.getNextLogicalTimestamp();
    const tombstoneLogicalTimestamp = this.getNextLogicalTimestamp();

    // Add metadata properties
    this.sourceOfTruth.addProperty({
      objectId,
      propertyName: 'object.metadata',
      value: {
        type: objectType,
        parent_id: parentId || null
      },
      lwwCounter: null, // Special properties don't use LWW
      lwwRandom: null,
      replicaId: this.replicaId,
      logicalTimestamp: metadataLogicalTimestamp,
      source: 'user',
      addedToMemoryAt: timestamp
    });

    // Note: object.tombstoned property is only created when actually tombstoning (value = true)
    // Absence of the property means the object is not tombstoned

    // Update UI state
    this.updateStateFromSourceOfTruth();
    
    this.state.isDirty = true;
    this.state.lastModified = timestamp;
    this.notifyListeners();
  }

  /**
   * Delete an object (mark as tombstoned)
   */
  deleteObject(objectId: string): void {

    const timestamp = Date.now();
    const logicalTimestamp = this.getNextLogicalTimestamp();
    
    // Add tombstone property directly (special properties don't use LWW)
    this.sourceOfTruth.addProperty({
      objectId,
      propertyName: 'object.tombstoned',
      value: true,
      lwwCounter: null, // Special properties don't use LWW
      lwwRandom: null,
      replicaId: this.replicaId,
      logicalTimestamp,
      source: 'user',
      addedToMemoryAt: timestamp
    });

    // Update UI state
    this.updateStateFromSourceOfTruth();
    
    this.state.isDirty = true;
    this.state.lastModified = timestamp;
    this.notifyListeners();
  }

  /**
   * Get a specific object by ID
   */
  getObject(objectId: string): UIDocumentObject | null {
    return this.state.objects.get(objectId) || null;
  }

  /**
   * Get all pages for this document
   */
  getPages(): UIDocumentObject[] {
    return this.state.pages;
  }

  /**
   * Get child objects of a parent
   */
  getChildren(parentId: string): UIDocumentObject[] {
    
    const allObjects = Array.from(this.state.objects.values());
    const childObjects = allObjects.filter(obj => obj.parent_id === parentId && !obj.is_tombstoned);
    
    
    return childObjects.sort((a, b) => {
      // Sort by position if available, otherwise by object_id
      const posA = parseFloat(a.position || '0');
      const posB = parseFloat(b.position || '0');
      if (posA !== posB) return posA - posB;
      return a.object_id.localeCompare(b.object_id);
    });
  }

  /**
   * Subscribe to state changes
   */
  subscribe(listener: DocumentStateListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /**
   * Force refresh state from source of truth
   */
  refreshState(): void {
    this.updateStateFromSourceOfTruth();
    this.notifyListeners();
  }

  /**
   * Check if document has unsaved changes
   */
  isDirty(): boolean {
    return this.state.isDirty;
  }

  /**
   * Mark document as clean (after successful sync/save)
   */
  markClean(): void {
    this.state.isDirty = false;
    this.notifyListeners();
  }

  // Private methods

  private setupReactiveUpdates(): void {
    // TODO: Set up listeners on InMemorySourceOfTruth for property changes
    // For now, we'll rely on manual updates after each change
  }

  private updateStateFromSourceOfTruth(): void {
    const objects = new Map<string, UIDocumentObject>();
    const allObjects = this.sourceOfTruth.getAllObjects();


    // Convert source of truth objects to UI objects (exclude tombstoned)
    for (const metadata of allObjects) {
      const uiObject = this.createUIObject(metadata);
      if (uiObject && !uiObject.is_tombstoned) {
        objects.set(metadata.objectId, uiObject);
      }
    }

    // Update document title from root object
    const titleProperty = this.sourceOfTruth.getObjectProperty(this.documentId, 'title');
    const documentTitle = titleProperty?.value || this.documentId;
    

    // Get pages (direct children of document that are not tombstoned)
    const pages = Array.from(objects.values())
      .filter(obj => obj.parent_id === this.documentId && obj.object_type === 'page' && !obj.is_tombstoned)
      .sort((a, b) => {
        const posA = parseFloat(a.position || '0');
        const posB = parseFloat(b.position || '0');
        if (posA !== posB) return posA - posB;
        return a.object_id.localeCompare(b.object_id);
      });

    // Update state
    this.state = {
      ...this.state,
      title: documentTitle,
      objects,
      pages,
      isLoaded: objects.size > 0
    };
  }

  /**
   * Get structural object IDs (document + pages only, no text blocks)
   * This implements the RFC partial loading strategy
   */
  private async getStructuralObjectIds(): Promise<string[]> {
    // Get all objects from SQLite to identify structural objects
    const allObjects = await this.sqliteService.getObjects();
    
    const structuralIds: string[] = [];
    
    for (const obj of allObjects) {
      // Include document root
      if (obj.object_id === this.documentId) {
        structuralIds.push(obj.object_id);
      }
      // Include pages (direct children of document)
      else if (obj.parent_id === this.documentId && obj.object_type === 'page') {
        structuralIds.push(obj.object_id);
      }
      // Skip text blocks and other deeper objects
    }
    
    return structuralIds;
  }

  private createUIObject(metadata: ObjectMetadata): UIDocumentObject | null {
    // Get object metadata
    const metadataProperty = this.sourceOfTruth.getObjectProperty(metadata.objectId, 'object.metadata');
    const metadataValue = metadataProperty?.value || {};
    
    // Check if tombstoned
    const tombstoneProperty = this.sourceOfTruth.getObjectProperty(metadata.objectId, 'object.tombstoned');
    const isTombstoned = tombstoneProperty?.value === true;
    
    // Build properties map
    const properties = new Map<string, any>();
    const objectProperties = this.sourceOfTruth.getObjectProperties(metadata.objectId);
    
    for (const prop of objectProperties) {
      // Skip special properties in the UI properties map
      if (!prop.propertyName.startsWith('object.')) {
        properties.set(prop.propertyName, prop.value);
      }
    }

    return {
      object_id: metadata.objectId,
      parent_id: metadata.parentId,
      object_type: metadata.objectType,
      is_tombstoned: isTombstoned, // Include tombstone status so UI can filter properly
      title: properties.get('title'),
      content: properties.get('text') || properties.get('content'), // Text blocks use 'text' property
      position: properties.get('position'),
      created_at: metadataValue.created_at,
      updated_at: metadataValue.updated_at,
      properties
    };
  }

  private getObjectProperty(objectId: string, propertyName: string): any {
    const property = this.sourceOfTruth.getObjectProperty(objectId, propertyName);
    return property?.value;
  }

  private notifyListeners(): void {
    const state = this.getState();
    this.listeners.forEach(listener => {
      try {
        listener(state);
      } catch (error) {
        console.error('Error in state listener:', error);
      }
    });
  }
}
